// Schedule workflows: the words for a schedule and the default a new one starts with.
// Mirrors `workflow_schedules/schedule.py` (`describe`).

import type { ScheduleTriggerConfig } from "../../api/client.ts";

export const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** The browser's time zone, else Oslo. */
export function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Oslo";
  } catch {
    return "Europe/Oslo";
  }
}

/** Every IANA zone the browser knows, the local one included. */
export function timeZones(): string[] {
  const local = localTimeZone();
  let zones: string[] = [];
  try {
    zones = Intl.supportedValuesOf("timeZone");
  } catch {
    zones = [];
  }
  return zones.includes(local) ? zones : [local, ...zones];
}

export function defaultSchedule(): ScheduleTriggerConfig {
  return { frequency: "weekly", time: "08:00", weekdays: [0, 1, 2, 3, 4], timezone: localTimeZone() };
}

function listOf(names: string[]): string {
  return names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** "Weekdays at 08:00", "Every hour at :15"; with the zone when `withZone`. */
export function scheduleLabel(config: ScheduleTriggerConfig, { withZone = true } = {}): string {
  const zone = withZone ? ` (${config.timezone})` : "";
  if (config.frequency === "hourly") {
    return `Every hour at :${String(config.minute ?? 0).padStart(2, "0")}${zone}`;
  }
  const at = config.time ?? "?";
  if (config.frequency === "daily") return `Every day at ${at}${zone}`;
  const days = [...new Set(config.weekdays ?? [])].sort((a, b) => a - b);
  const key = days.join(",");
  const when =
    key === "0,1,2,3,4,5,6"
      ? "Every day"
      : key === "0,1,2,3,4"
        ? "Weekdays"
        : key === "5,6"
          ? "Weekends"
          : days.length
            ? listOf(days.map((d) => `${WEEKDAY_NAMES[d]}s`))
            : "No days";
  return `${when} at ${at}${zone}`;
}

/** Why the schedule cannot be saved, or null. */
export function scheduleProblem(config: ScheduleTriggerConfig): string | null {
  if (config.frequency === "hourly") {
    const m = config.minute;
    return m === undefined || !Number.isInteger(m) || m < 0 || m > 59 ? "Pick a minute from 0 to 59" : null;
  }
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(config.time ?? "")) return "Pick a time of day";
  if (config.frequency === "weekly" && !(config.weekdays ?? []).length) return "Pick at least one day";
  if (!config.timezone) return "Pick a time zone";
  return null;
}
